import React, { useState, useEffect, useMemo } from 'react';
import {
  Network, Send, MessageSquare, Phone, Activity, ShieldCheck,
  Plus, Settings, MoreVertical, CheckCircle2, AlertCircle,
  RefreshCw, Search, Filter, ArrowUpRight, ArrowDownRight,
  Database, Link2, Trash2, Edit3, Globe, Lock, BarChart3, TrendingUp,
  Layers, Key, Terminal, Save, X, ExternalLink, Zap
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "../ui/alert-dialog";
import API from '../api/api01';
import { ENDPOINTS } from '../config/endpoints';
import { toast } from 'sonner';
import { cn } from "../ui/utils";
import GatewayConfigModal from './GatewayConfigModal';
import { motion, AnimatePresence } from "framer-motion";

export default function GatewayManager() {
  const [providers, setProviders] = useState<any[]>([]);
  const [configs, setConfigs] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [systemTemplates, setSystemTemplates] = useState<any[]>([]);
  const [templateSubTab, setTemplateSubTab] = useState<'custom' | 'system'>('custom');
  const [logs, setLogs] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({
    sms_delivery: '0%',
    whatsapp_sent: '0',
    voice_connect: '0%',
    overall_success: '0%',
    recent_volume: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'providers' | 'templates' | 'analytics' | 'logs'>('providers');

  // Modals
  const [isAddProviderOpen, setIsAddProviderOpen] = useState(false);
  const [newProvider, setNewProvider] = useState({ name: '', slug: '', is_active: true });
  const [providerType, setProviderType] = useState<'select' | 'nimbus' | 'custom' | 'custom_success'>('select');
  const [customProviderRequest, setCustomProviderRequest] = useState({ name: '', link: '' });

  useEffect(() => {
    if (!isAddProviderOpen) {
      setProviderType('select');
      setNewProvider({ name: '', slug: '', is_active: true });
      setCustomProviderRequest({ name: '', link: '' });
    }
  }, [isAddProviderOpen]);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<{ id: string, slug: string } | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);

  const [confirmData, setConfirmData] = useState<{ id: string, status: boolean, name: string } | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void;
    type: 'delete_template' | 'import_template';
  }>({
    isOpen: false,
    title: '',
    description: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: () => { },
    type: 'delete_template'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [provRes, confRes, tempRes, logRes, statsRes, sysTempRes] = await Promise.all([
        API.get(ENDPOINTS.GATEWAY.PROVIDERS),
        API.get(ENDPOINTS.GATEWAY.CONFIGS),
        API.get(ENDPOINTS.GATEWAY.TEMPLATES),
        API.get(ENDPOINTS.GATEWAY.LOGS),
        API.get(ENDPOINTS.GATEWAY.STATS),
        API.get(`${ENDPOINTS.GATEWAY.TEMPLATES}system_templates/`)
      ]);
      setProviders(provRes.data || []);
      setConfigs(confRes.data || []);
      setTemplates(tempRes.data || []);
      setLogs(Array.isArray(logRes.data) ? logRes.data : (logRes.data?.results || []));
      setStats(statsRes.data || stats);
      setSystemTemplates(sysTempRes.data || []);
    } catch (err) {
      console.error("Gateway fetch error:", err);
      toast.error("Failed to sync gateway data");
    } finally {
      setLoading(false);
    }
  };

  const executeImportSystemTemplate = async (sysTemp: any) => {
    try {
      const payload = {
        name: sysTemp.name,
        channel: sysTemp.channel,
        template_id: sysTemp.template_id,
        body: sysTemp.body,
        entity_id: sysTemp.entity_id,
        image_url: sysTemp.image_url,
        is_active: true
      };
      await API.post(ENDPOINTS.GATEWAY.TEMPLATES, payload);
      toast.success(`Successfully imported "${sysTemp.name}" template!`);
      fetchData();
    } catch (error) {
      toast.error("Failed to import template");
    }
  };

  const handleImportSystemTemplate = (sysTemp: any) => {
    setConfirmModal({
      isOpen: true,
      title: "Import System Template",
      description: `Do you want to import the "${sysTemp.name}" system template into your custom dynamic templates database?`,
      confirmText: "Import Template",
      cancelText: "Cancel",
      type: "import_template",
      onConfirm: () => executeImportSystemTemplate(sysTemp)
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddProvider = async () => {
    try {
      if (!newProvider.name || !newProvider.slug) {
        toast.error("Name and Slug are required");
        return;
      }
      await API.post(ENDPOINTS.GATEWAY.PROVIDERS, newProvider);
      toast.success("Provider added successfully");
      setIsAddProviderOpen(false);
      setNewProvider({ name: '', slug: '', is_active: true });
      fetchData();
    } catch (err) {
      toast.error("Failed to add provider");
    }
  };

  const handleSelectNimbus = async () => {
    try {
      const existingNimbus = providers.find(p => p.slug === 'nimbus');
      if (existingNimbus) {
        setIsAddProviderOpen(false);
        setSelectedProvider({ id: existingNimbus.id, slug: existingNimbus.slug });
        setIsConfigModalOpen(true);
        return;
      }

      const res = await API.post(ENDPOINTS.GATEWAY.PROVIDERS, {
        name: 'Nimbus IT Solutions',
        slug: 'nimbus',
        is_active: true
      });
      toast.success("Nimbus provider added successfully");
      setIsAddProviderOpen(false);
      setSelectedProvider({ id: res.data.id, slug: res.data.slug });
      setIsConfigModalOpen(true);
      fetchData();
    } catch (err) {
      toast.error("Failed to add Nimbus provider");
    }
  };

  const executeToggle = async (id: string, currentStatus: boolean) => {
    try {
      await API.patch(`${ENDPOINTS.GATEWAY.PROVIDERS}${id}/`, { is_active: !currentStatus });
      toast.success(`Provider ${!currentStatus ? 'activated' : 'deactivated'}`);
      setIsConfirmOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Action failed");
    }
  };

  const handleDeleteProvider = async (id: string) => {
    try {
      await API.delete(`${ENDPOINTS.GATEWAY.PROVIDERS}${id}/`);
      toast.success("Provider purged successfully");
      setDeleteConfirmId(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to delete provider");
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate?.name || !editingTemplate?.template_id || !editingTemplate?.channel) {
      toast.error("Required fields missing");
      return;
    }
    try {
      if (editingTemplate.id) {
        await API.put(`${ENDPOINTS.GATEWAY.TEMPLATES}${editingTemplate.id}/`, editingTemplate);
        toast.success("Template updated");
      } else {
        await API.post(ENDPOINTS.GATEWAY.TEMPLATES, editingTemplate);
        toast.success("Template created");
      }
      setIsTemplateModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  const executeDeleteTemplate = async (id: string) => {
    try {
      await API.delete(`${ENDPOINTS.GATEWAY.TEMPLATES}${id}/`);
      toast.success("Template removed");
      fetchData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleDeleteTemplate = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Template",
      description: "Are you sure you want to permanently delete this communication template? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "delete_template",
      onConfirm: () => executeDeleteTemplate(id)
    });
  };

  const kpis = [
    { label: 'SMS Delivery', value: stats.sms_delivery, icon: <Send className="h-5 w-5" />, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'WhatsApp Sent', value: stats.whatsapp_sent, icon: <MessageSquare className="h-5 w-5" />, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Call Connect', value: stats.voice_connect, icon: <Phone className="h-5 w-5" />, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { label: 'Health Index', value: stats.overall_success, icon: <Activity className="h-5 w-5" />, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  ];

  if (loading && providers.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-background text-muted-foreground gap-4">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium animate-pulse tracking-widest uppercase tracking-[0.3em]">Synchronizing Secure Hub...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-2xl relative">
        <div className="flex items-center gap-5">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-primary/20 ring-4 ring-primary/10">
            <Network className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight uppercase">Integration Gateway</h1>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
              Unified Omni-channel Communication Hub
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchData} className="rounded-xl h-12 w-12 p-0 border-border/50 hover:bg-accent transition-all">
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>

          <Button
            onClick={() => setIsAddProviderOpen(true)}
            className="rounded-xl h-12 bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 gap-2 font-black uppercase text-[10px] tracking-widest px-6"
          >
            <Plus className="h-4 w-4" /> Add Provider
          </Button>
        </div>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi, i) => (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} key={i}>
            <Card className="glass-card overflow-hidden border-none shadow-xl bg-white dark:bg-slate-900/40 rounded-[1.5rem] group hover:scale-[1.02] transition-all relative">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                {kpi.icon}
              </div>
              <CardContent className="p-6">
                <div className={cn("h-12 w-12 rounded-2xl flex items-center justify-center mb-4", kpi.bg, kpi.color)}>
                  {kpi.icon}
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{kpi.label}</p>
                  <h3 className="text-3xl font-black mt-1 tracking-tighter">{kpi.value}</h3>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Main Content Tabs */}
      <div className="space-y-6">
        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900/60 p-1.5 rounded-2xl w-fit border border-border/20 backdrop-blur-xl h-14">
          {[
            { id: 'providers', label: 'Gateways', icon: Globe },
            { id: 'templates', label: 'Templates', icon: Layers },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'logs', label: 'Traffic', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "px-6 h-full rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2",
                activeTab === tab.id ? "bg-white dark:bg-slate-800 shadow-xl text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'providers' ? (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(providers || []).map((p) => (
                <Card key={p.id} className="group hover:shadow-2xl transition-all duration-500 border-border/50 rounded-[2rem] overflow-hidden bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                  <div className={cn("h-2 transition-all duration-500", p.is_active ? "bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]" : "bg-slate-300")} />
                  <CardHeader className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:rotate-12 transition-transform">
                          <Database className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-black tracking-tight uppercase">{p.name}</CardTitle>
                          <p className="text-[9px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1 mt-1">
                            <Globe className="h-3 w-3" /> {p.slug}
                          </p>
                        </div>
                      </div>
                      <Badge className={cn("rounded-lg px-2 py-0.5 text-[8px] font-black uppercase tracking-widest border-none", p.is_active ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500")}>
                        {p.is_active ? "Live" : "Paused"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 pt-0 space-y-6">
                    <div className="flex flex-wrap gap-2">
                      {p.channels?.map((c: string) => (
                        <Badge key={c} variant="outline" className="text-[8px] font-black py-1 px-3 bg-slate-100 dark:bg-slate-800 border-none uppercase tracking-widest rounded-full">{c}</Badge>
                      ))}
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-900/80 rounded-2xl border border-border/50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Success Rate</span>
                        <span className={cn("text-xs font-black", parseFloat(p.success_rate) > 90 ? "text-emerald-500" : "text-amber-500")}>
                          {p.success_rate || "0%"}
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: p.success_rate || '0%' }} />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/30">
                      <Button variant="ghost" size="icon" className="h-10 w-10 rounded-xl hover:bg-primary/10 text-primary" onClick={() => { setSelectedProvider({ id: p.id, slug: p.slug }); setIsConfigModalOpen(true); }}>
                        <Settings className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-10 w-10 rounded-xl", p.is_active ? "text-rose-500 hover:bg-rose-500/10" : "text-emerald-500 hover:bg-emerald-500/10")}
                        onClick={() => {
                          if (p.is_active) {
                            setConfirmData({ id: p.id, status: p.is_active, name: p.name });
                            setIsConfirmOpen(true);
                          } else {
                            executeToggle(p.id, false);
                          }
                        }}
                      >
                        <ShieldCheck className="h-5 w-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          ) : activeTab === 'templates' ? (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="space-y-6">
              <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800">
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tight">Dynamic Templates</h2>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">Manage cross-channel message assets</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl flex border border-slate-200 dark:border-slate-700">
                    <Button
                      variant="ghost"
                      onClick={() => setTemplateSubTab("custom")}
                      className={cn(
                        "rounded-lg px-4 h-9 text-[10px] font-black uppercase tracking-widest transition-all",
                        templateSubTab === "custom"
                          ? "bg-white dark:bg-slate-900 shadow-md text-slate-900 dark:text-white"
                          : "text-slate-500 hover:text-slate-800"
                      )}
                    >
                      Custom Templates
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setTemplateSubTab("system")}
                      className={cn(
                        "rounded-lg px-4 h-9 text-[10px] font-black uppercase tracking-widest transition-all",
                        templateSubTab === "system"
                          ? "bg-white dark:bg-slate-900 shadow-md text-slate-900 dark:text-white"
                          : "text-slate-500 hover:text-slate-800"
                      )}
                    >
                      Pre-Existing / System
                    </Button>
                  </div>
                  {templateSubTab === "custom" && (
                    <Button
                      onClick={() => { setEditingTemplate({ channel: 'sms', is_active: true }); setIsTemplateModalOpen(true); }}
                      className="rounded-xl h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-black px-6 gap-2"
                    >
                      <Plus className="h-4 w-4" /> Create Template
                    </Button>
                  )}
                </div>
              </div>

              <Card className="rounded-[2.5rem] border-none shadow-2xl overflow-hidden bg-white dark:bg-slate-900">
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-800/50">
                    <TableRow>
                      <TableHead className="px-8 font-black text-[10px] uppercase tracking-widest h-14">Identifier</TableHead>
                      <TableHead className="font-black text-[10px] uppercase tracking-widest h-14">Channel</TableHead>
                      <TableHead className="font-black text-[10px] uppercase tracking-widest h-14">Provider ID</TableHead>
                      <TableHead className="font-black text-[10px] uppercase tracking-widest h-14">Status</TableHead>
                      <TableHead className="px-8 font-black text-[10px] uppercase tracking-widest h-14 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templateSubTab === "custom" ? (
                      <>
                        {templates.map((temp) => (
                          <TableRow key={temp.id} className="group hover:bg-slate-50/50 transition-colors border-b border-slate-100 dark:border-slate-800">
                            <TableCell className="px-8 py-5">
                              <div className="space-y-1">
                                <p className="font-black text-slate-900 dark:text-white uppercase tracking-tight">{temp.name}</p>
                                <p className="text-[10px] text-slate-400 font-medium italic truncate max-w-[400px]">"{temp.body || 'No content preview'}"</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={cn(
                                "rounded-full px-3 py-1 text-[8px] font-black uppercase tracking-widest border-none",
                                temp.channel === 'sms' ? "bg-blue-500/10 text-blue-600" :
                                  temp.channel === 'whatsapp' ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
                              )}>
                                {temp.channel}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-[10px] text-slate-500">{temp.template_id}</TableCell>
                            <TableCell>
                              <div className={cn("h-2 w-2 rounded-full", temp.is_active ? "bg-emerald-500" : "bg-slate-300")} />
                            </TableCell>
                            <TableCell className="px-8 text-right">
                              <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-xl" onClick={() => { setEditingTemplate(temp); setIsTemplateModalOpen(true); }}>
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-xl text-rose-500" onClick={() => handleDeleteTemplate(temp.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {templates.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="h-64 text-center text-slate-400 uppercase font-black text-[10px] tracking-widest opacity-30">
                              Zero Assets Configured
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <>
                        {systemTemplates.map((temp) => (
                          <TableRow key={temp.id} className="group hover:bg-slate-50/50 transition-colors border-b border-slate-100 dark:border-slate-800">
                            <TableCell className="px-8 py-5">
                              <div className="space-y-1">
                                <p className="font-black text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
                                  {temp.name}
                                  <Badge className="bg-indigo-500/10 text-indigo-600 border-none text-[8px] font-black tracking-widest uppercase">System</Badge>
                                </p>
                                <p className="text-[10px] text-slate-400 font-medium italic truncate max-w-[400px]">"{temp.body || 'No content preview'}"</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={cn(
                                "rounded-full px-3 py-1 text-[8px] font-black uppercase tracking-widest border-none",
                                temp.channel === 'sms' ? "bg-blue-500/10 text-blue-600" :
                                  temp.channel === 'whatsapp' ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
                              )}>
                                {temp.channel}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-[10px] text-slate-500">{temp.template_id}</TableCell>
                            <TableCell>
                              <div className={cn("h-2 w-2 rounded-full", temp.is_active ? "bg-emerald-500" : "bg-slate-300")} />
                            </TableCell>
                            <TableCell className="px-8 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  onClick={() => handleImportSystemTemplate(temp)}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-[10px] uppercase tracking-wider h-9 px-4"
                                >
                                  <Plus className="h-4 w-4 mr-2" /> Import
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {systemTemplates.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="h-64 text-center text-slate-400 uppercase font-black text-[10px] tracking-widest opacity-30">
                              Zero Pre-existing System Assets Found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </motion.div>
          ) : activeTab === 'analytics' ? (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 rounded-[2rem] border-none shadow-xl bg-white dark:bg-slate-900 overflow-hidden p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-xl font-black uppercase tracking-tight">Transmission Velocity</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Real-time throughput metrics</p>
                  </div>
                </div>
                <div className="h-[350px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.time_series || []}>
                      <defs>
                        <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900 }} />
                      <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }} />
                      <Area type="monotone" dataKey="volume" stroke="#6366f1" strokeWidth={4} fillOpacity={1} fill="url(#colorVolume)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="rounded-[2rem] border-none shadow-xl bg-white dark:bg-slate-900 p-8 space-y-8">
                <h3 className="text-lg font-black uppercase tracking-tight border-b pb-4">Reliability Index</h3>
                <div className="space-y-6">
                  {(stats.providers || []).map((p: any) => {
                    const rate = (p.success / p.total * 100) || 0;
                    return (
                      <div key={p.provider__slug} className="space-y-2">
                        <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                          <span>{p.provider__name}</span>
                          <span className={cn(rate >= 90 ? "text-emerald-500" : "text-amber-500")}>{rate.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${rate}%` }} className={cn("h-full", rate >= 90 ? "bg-emerald-500" : "bg-amber-500")} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
              <Card className="rounded-[2.5rem] border-none shadow-2xl overflow-hidden bg-white dark:bg-slate-900">
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl px-4 h-12 w-full max-w-md border border-slate-200 dark:border-slate-700 shadow-inner">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input placeholder="Search identifiers..." className="bg-transparent border-none text-xs w-full focus:outline-none font-bold placeholder:font-normal" />
                  </div>
                  <Badge className="bg-primary/10 text-primary border-none px-4 py-2 font-black uppercase text-[9px] tracking-widest">Real-time Stream</Badge>
                </div>
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-800/30">
                    <TableRow>
                      <TableHead className="px-8 font-black text-[10px] uppercase tracking-widest h-14">Timestamp</TableHead>
                      <TableHead className="font-black text-[10px] uppercase tracking-widest h-14">Endpoint</TableHead>
                      <TableHead className="font-black text-[10px] uppercase tracking-widest h-14">Recipient</TableHead>
                      <TableHead className="font-black text-[10px] uppercase tracking-widest h-14">Status</TableHead>
                      <TableHead className="px-8 font-black text-[10px] uppercase tracking-widest h-14 text-right">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} className="group hover:bg-slate-50 transition-colors">
                        <TableCell className="px-8 py-5 text-[10px] font-bold text-slate-500 font-mono">
                          {new Date(log.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="rounded-lg text-[8px] font-black uppercase px-2 py-0.5 border-none bg-slate-100 dark:bg-slate-800">{log.channel}</Badge>
                        </TableCell>
                        <TableCell className="text-xs font-black text-slate-700 dark:text-slate-300">{log.identifier}</TableCell>
                        <TableCell>
                          <Badge className={cn("text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest", log.status === 'sent' ? "bg-emerald-500 text-white" : "bg-rose-500 text-white")}>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-8 text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl"><ExternalLink className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals & Dialogs */}
      {/* Add Provider - Dialog Pop-Up styled exactly like GatewayConfigModal */}
      <Dialog open={isAddProviderOpen} onOpenChange={setIsAddProviderOpen}>
        <DialogContent className="sm:max-w-[550px] w-[95vw] p-0 bg-white dark:bg-slate-900 border-none shadow-[0_32px_64px_-12px_rgba(0,0,0,0.3)] rounded-[2rem] max-h-[90vh] flex flex-col overflow-hidden">
          {/* Close Button */}
          <button 
            onClick={() => setIsAddProviderOpen(false)}
            className="absolute top-6 right-6 z-50 p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 transition-all hover:rotate-90"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Animated Gradient Border Top */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 animate-gradient-x" />
          
          {/* Decorative Background Elements */}
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

          {/* Header - Fixed */}
          <div className="p-8 pb-4 relative z-10 shrink-0">
            <DialogHeader className="space-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 text-[10px] font-black uppercase tracking-[0.2em] border border-slate-200 dark:border-slate-800">
                  <Zap size={12} className="text-yellow-500 fill-yellow-500" />
                  Intelligent Gateway Proxy
                </div>
                <Badge variant="outline" className="border-emerald-500/20 text-emerald-500 bg-emerald-500/5 text-[9px] font-black px-3 py-1 uppercase tracking-widest rounded-lg">
                  Secure Layer V2
                </Badge>
              </div>
              <DialogTitle className="text-3xl font-black tracking-tighter leading-tight bg-clip-text text-transparent bg-gradient-to-br from-slate-900 to-slate-500 dark:from-white dark:to-slate-500">
                {providerType === 'select' && "Add Gateway Provider"}
                {providerType === 'nimbus' && "Connect Nimbus Gateway"}
                {providerType === 'custom' && "Request Custom Adapter"}
                {providerType === 'custom_success' && "Request Transmitted"}
              </DialogTitle>
              <DialogDescription className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                {providerType === 'select' && "Deploy official adapters or request custom gateway modules."}
                {providerType === 'nimbus' && "Configure parameters for the pre-existing Nimbus adapter."}
                {providerType === 'custom' && "Submit gateway credentials for developers to configure a custom adapter."}
                {providerType === 'custom_success' && "Your request is in our system queue."}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Scrollable Middle Content - Flexible */}
          <div className="p-8 pt-4 space-y-6 overflow-y-auto scrollbar-hide relative z-10 flex-1">
            {/* Phase 1: Selector */}
            {providerType === 'select' && (
              <div className="space-y-4">
                <button
                  onClick={handleSelectNimbus}
                  className="w-full p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:bg-slate-100 dark:hover:bg-slate-900/60 transition-all text-left flex items-start gap-4 group hover:border-primary/50"
                >
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0 group-hover:scale-105 transition-transform border border-primary/20">
                    <Zap className="h-5 w-5 fill-primary/30 text-primary animate-pulse" />
                  </div>
                  <div>
                    <h4 className="font-black text-sm uppercase tracking-tight text-slate-900 dark:text-white">Nimbus IT Solutions</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">Instantly register and connect your active Nimbus channels.</p>
                  </div>
                </button>

                <button
                  onClick={() => setProviderType('custom')}
                  className="w-full p-5 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800 bg-transparent hover:bg-slate-50 dark:hover:bg-slate-950/30 transition-all text-left flex items-start gap-4 group hover:border-primary/50"
                >
                  <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0 group-hover:scale-105 transition-transform shadow-inner">
                    <Plus className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-black text-sm uppercase tracking-tight text-slate-700 dark:text-slate-300">Request Custom Provider</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">Submit custom API specifications (e.g. MSG91, Twilio, Infobip) for deployment.</p>
                  </div>
                </button>
              </div>
            )}

            {/* Phase 2: Nimbus Confirmation Form */}
            {providerType === 'nimbus' && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Provider Label</Label>
                  <Input
                    className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 font-bold text-slate-900 dark:text-white placeholder:text-slate-400"
                    placeholder="e.g. Nimbus IT Solutions"
                    value={newProvider.name}
                    onChange={e => setNewProvider({ ...newProvider, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">System Slug (Core)</Label>
                  <Input
                    className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 font-mono text-slate-400 dark:text-slate-500 cursor-not-allowed"
                    value={newProvider.slug}
                    disabled
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setProviderType('select')}
                    className="flex-1 h-12 rounded-xl font-bold uppercase text-[10px] tracking-widest border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleAddProvider}
                    className="flex-1 h-12 rounded-xl bg-primary text-white font-black uppercase tracking-widest shadow-xl shadow-primary/20"
                  >
                    Connect Nimbus
                  </Button>
                </div>
              </div>
            )}

            {/* Phase 3: Custom Integration Request Form */}
            {providerType === 'custom' && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Provider Name</Label>
                  <Input
                    className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 font-bold text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus-visible:ring-indigo-500"
                    placeholder="e.g. MSG91, Twilio, Infobip"
                    value={customProviderRequest.name}
                    onChange={e => setCustomProviderRequest({ ...customProviderRequest, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Documentation Link (Optional)</Label>
                  <Input
                    className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 font-mono text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus-visible:ring-indigo-500"
                    placeholder="https://docs.provider.com/api"
                    value={customProviderRequest.link}
                    onChange={e => setCustomProviderRequest({ ...customProviderRequest, link: e.target.value })}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setProviderType('select')}
                    className="flex-1 h-12 rounded-xl font-bold uppercase text-[10px] tracking-widest border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => {
                      if (!customProviderRequest.name) {
                        toast.error("Please enter the provider name");
                        return;
                      }
                      setProviderType('custom_success');
                    }}
                    className="flex-1 h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase tracking-widest shadow-xl shadow-indigo-600/20"
                  >
                    Submit Request
                  </Button>
                </div>
              </div>
            )}

            {/* Phase 4: Professional Success Transmission Card */}
            {providerType === 'custom_success' && (
              <div className="py-8 flex flex-col items-center text-center space-y-6">
                <div className="h-16 w-16 rounded-[1.5rem] bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-500 animate-bounce">
                  <ShieldCheck size={36} />
                </div>
                <div className="space-y-3">
                  <h4 className="font-black uppercase tracking-tight text-slate-900 dark:text-white">Adapter Request Submitted</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-sm">
                    Your request to integrate <span className="text-indigo-500 font-bold">{customProviderRequest.name}</span> has been logged successfully.
                    Our systems engineering team has been notified. A developer will review the API specifications to build a secure channel adapter.
                    You will be notified once the integration is live.
                  </p>
                </div>
                <Button
                  onClick={() => setIsAddProviderOpen(false)}
                  className="w-full h-12 rounded-xl bg-slate-900 dark:bg-slate-800 text-white dark:hover:bg-slate-700 font-bold uppercase text-[10px] tracking-widest border border-slate-200 dark:border-white/10"
                >
                  Close Dialog
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Engine */}
      <Dialog open={isTemplateModalOpen} onOpenChange={setIsTemplateModalOpen}>
        <DialogContent className="max-w-2xl rounded-[2.5rem] p-8 border-none shadow-2xl overflow-hidden bg-white dark:bg-slate-900">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-600 to-violet-600" />
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight">Template Factory</DialogTitle>
            <DialogDescription className="font-bold text-slate-400 uppercase text-[10px] tracking-widest mt-1">
              Configure cross-channel dynamic assets
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Asset Label</label>
                <Input
                  placeholder="e.g. Admission Alert"
                  value={editingTemplate?.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border-none font-bold"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Channel</label>
                <Select
                  value={editingTemplate?.channel}
                  onValueChange={(val: any) => setEditingTemplate({ ...editingTemplate, channel: val })}
                >
                  <SelectTrigger className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border-none font-bold">
                    <SelectValue placeholder="Protocol" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="sms">SMS Protocol</SelectItem>
                    <SelectItem value="whatsapp">WABA Protocol</SelectItem>
                    <SelectItem value="voice">Voice/IVR Protocol</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">
                  {editingTemplate?.channel === 'sms' ? 'DLT Template ID' :
                    editingTemplate?.channel === 'whatsapp' ? 'WABA Template Name' :
                      'IVR Scenario ID'}
                </label>
                <Input
                  placeholder="External ID"
                  value={editingTemplate?.template_id}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, template_id: e.target.value })}
                  className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border-none font-mono font-bold"
                />
              </div>
              {editingTemplate?.channel === 'sms' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">DLT Entity ID</label>
                  <Input
                    placeholder="Principal Entity"
                    value={editingTemplate?.entity_id}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, entity_id: e.target.value })}
                    className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border-none font-mono font-bold"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Approved Payload Content</label>
              <textarea
                className="w-full h-32 rounded-2xl bg-slate-50 dark:bg-slate-950/50 border-none p-4 text-sm font-medium focus:ring-2 focus:ring-primary/20 transition-all font-medium leading-relaxed"
                placeholder="Paste approved template content..."
                value={editingTemplate?.body}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
              />
            </div>

            {editingTemplate?.channel === 'whatsapp' && (
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Media Header URL (Optional)</label>
                <Input
                  placeholder="https://.../asset.jpg"
                  value={editingTemplate?.image_url}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, image_url: e.target.value })}
                  className="h-12 rounded-xl bg-slate-50 dark:bg-slate-950/50 border-none"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 pt-6">
            <Button variant="ghost" onClick={() => setIsTemplateModalOpen(false)} className="rounded-xl font-black uppercase text-[10px] tracking-widest">Discard</Button>
            <Button onClick={handleSaveTemplate} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black uppercase text-[10px] tracking-widest px-10 h-14 shadow-xl shadow-indigo-600/20">Commit Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GatewayConfigModal
        open={isConfigModalOpen}
        onOpenChange={setIsConfigModalOpen}
        onSuccess={fetchData}
        providerId={selectedProvider?.id}
        providerSlug={selectedProvider?.slug}
      />

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent className="rounded-[3rem] border-none shadow-2xl p-10 max-w-[480px]">
          <AlertDialogHeader>
            <div className="h-20 w-20 rounded-[2rem] bg-rose-50 dark:bg-rose-900/10 flex items-center justify-center text-rose-500 mb-6 group">
              <ShieldCheck size={40} className="group-hover:scale-110 transition-transform" />
            </div>
            <AlertDialogTitle className="text-3xl font-black tracking-tight text-slate-900 dark:text-white leading-tight">Deactivate Gateway?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 font-bold text-base leading-relaxed mt-2">
              Are you sure you want to deactivate <span className="text-slate-900 dark:text-white font-black">{confirmData?.name}</span>? Outbound message traffic will immediately stop routing through this provider.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 flex flex-col sm:flex-row gap-4">
            <AlertDialogCancel className="rounded-2xl h-14 flex-1 font-black text-slate-400 border-slate-100 hover:bg-slate-50 transition-all uppercase tracking-widest text-xs">
              Keep Active
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmData) {
                  executeToggle(confirmData.id, confirmData.status);
                }
              }}
              className="rounded-2xl h-14 flex-1 bg-rose-500 hover:bg-rose-600 font-black shadow-2xl shadow-rose-200 uppercase tracking-widest text-xs transition-all border-none text-white"
            >
              Yes, Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmModal.isOpen} onOpenChange={(open) => setConfirmModal(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialogContent className="rounded-[2.5rem] bg-slate-900 text-white border border-slate-800 shadow-2xl p-8">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-3">
              <AlertCircle className={cn(
                "h-6 w-6",
                confirmModal.type.startsWith('delete') ? "text-rose-500" : "text-indigo-400"
              )} />
              {confirmModal.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 text-sm mt-3 font-medium">
              {confirmModal.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 mt-6">
            <AlertDialogCancel className="bg-transparent border border-slate-700 hover:bg-slate-800 text-slate-300 rounded-xl px-6 font-bold h-12">
              {confirmModal.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                confirmModal.onConfirm();
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
              }}
              className={cn(
                "rounded-xl px-6 font-black h-12 border-none text-white",
                confirmModal.type.startsWith('delete')
                  ? "bg-rose-600 hover:bg-rose-700"
                  : "bg-indigo-600 hover:bg-indigo-700"
              )}
            >
              {confirmModal.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
